import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import {
	Box,
	Button,
	Checkbox,
	Flex,
	HStack,
	Input,
	SimpleGrid,
	Skeleton,
	Spinner,
	Text,
	Textarea,
} from '@chakra-ui/react';
import { RiUploadLine, RiTeamLine, RiFileExcel2Line, RiUserSearchLine } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { useFetchItemsQuery } from 'api/apiSlice';
import { validationLeadSearchSchema } from 'schema/leadSchema';
import { Banner, SectionCard } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import { uploadAudience } from '../uploadAudience';
import campaignError from '../campaignErrors';
import SegmentBar from './SegmentBar';
import { ChoiceCard } from '../_ui/controls';

// The lead list's own advanced search, lazily — it is a large form and only
// one of the two audience sources needs it.
const LazyAdvancedSearchForm = React.lazy(() => import('views/admin/lead-v2/components/AdvancedSearchForm'));

/**
 * Who this campaign goes to.
 *
 * ★ TWO SOURCES, AND THEY ARE NOT THE SAME KIND OF THING. A CRM audience is a
 * saved filter resolved with the CREATOR'S OWN lead visibility, so it changes as
 * the CRM changes and can never reach a lead this person could not already see.
 * An uploaded list is a one-off snapshot of numbers somebody typed, which is why
 * it needs a written attestation and the CRM audience does not.
 *
 * ★ THE FILTER IS THE LEAD LIST'S ADVANCED SEARCH — THE SAME FORM, NOT A COPY.
 * The server (`audience.service.streamCrm`) hands `leadFilter` to
 * `getAllLeadsService` as the lead list's own `data` object, verbatim. So the
 * form that builds it here is the lead list's `AdvancedSearchForm`, exactly as
 * the old bulk-message flow embeds it: real status and sub-status lists, country
 * and nationality pickers, agent and manager trees, date ranges. The eight
 * free-text boxes this replaced could only ever guess at spellings.
 *
 * ★ WHAT MATCHES IS SHOWN WHILE YOU TYPE. A filter is an abstraction; "34 leads
 * match, here are six of them" is a fact. The count comes from the same query
 * the planner will run, so what you see is what will be built.
 */

/**
 * Every field the advanced search form reads, so none is uncontrolled.
 *
 * Kept as a list rather than derived from the form, because the form reads
 * `values[name]` for each and React warns on the first render that sees an
 * undefined one flip to a string.
 */
const EMPTY_FILTER = Object.freeze({
	intID: '', leadName: '', leadEmail: '', leadPhoneNumber: '', leadWhatsappNumber: '',
	nationality: '', country: '', countryCode: '', city: '', leadAddress: '',
	eLeadStatus: '', leadStatus: '', mainStatusSort: '', whatsappStatus: '', isReleased: '',
	managerAssigned: '', agentAssigned: '',
	leadCampaign: '', leadSource: '', leadSourceDetails: '', leadSourceMedium: '', leadSourceChannel: '',
	adset: '', pageUrl: '', ip: '', r_u_in_uae: '', leadLang: '', timetocall: '', budget: '',
	lastNote: '', attendanceDay: '', startDate: '', endDate: '', fromLeadScore: '', toLeadScore: '',
});

/** Drop the blanks: the server treats a key as a constraint, and '' would match nothing. */
const cleaned = (values) =>
	Object.fromEntries(Object.entries(values ?? {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));

const same = (a, b) => JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});

/** How long after the last keystroke the draft and the live count update. */
const SETTLE_MS = 500;

function useDebounced(value, ms) {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setSettled(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return settled;
}

const readUser = () => {
	try {
		return JSON.parse(localStorage.getItem('user')) ?? {};
	} catch (_) {
		return {};
	}
};

/** The six-row glimpse of who the current filter reaches. */
function MatchPreview({ filterJson, enabled }) {
	const ui = useCampaignUI();
	const { data, isFetching } = useFetchItemsQuery(
		{ path: '/lead/v2', params: { page: 1, pageSize: 6, ...(filterJson && { data: filterJson }) } },
		{ skip: !enabled, refetchOnMountOrArgChange: true },
	);
	const leads = data?.doc ?? [];
	const total = data?.totalLeads ?? (Array.isArray(data?.doc) ? data.doc.length : null);

	return (
		<Box
			mt={5}
			borderWidth='1px'
			borderColor={ui.borderSubtle}
			borderRadius='xl'
			bg={ui.surfaceSubtle}
			overflow='hidden'
		>
			<Flex align='center' justify='space-between' px={4} py={3} gap={3} wrap='wrap'>
				<HStack spacing={3}>
					<Flex align='center' justify='center' boxSize='36px' borderRadius='lg' bg={ui.accentSubtle} color={ui.accentInk}>
						<RiUserSearchLine size={18} />
					</Flex>
					<Box>
						<HStack spacing={2} align='baseline'>
							{isFetching ? (
								<Spinner size='xs' color={ui.accentInk} />
							) : (
								<Text fontSize='xl' fontWeight='bold' color={ui.textPrimary} lineHeight='1'>
									{total === null ? '—' : Number(total).toLocaleString()}
								</Text>
							)}
							<Text fontSize='sm' color={ui.textSecondary}>
								{total === 1 ? 'lead matches' : 'leads match'} right now
							</Text>
						</HStack>
						<Text fontSize='xs' color={ui.textMuted}>
							Your own visibility, the same way the lead list counts. Consent and WhatsApp checks come at Review.
						</Text>
					</Box>
				</HStack>
			</Flex>

			{leads.length > 0 && (
				<Box borderTopWidth='1px' borderColor={ui.borderSubtle}>
					{leads.map((lead, i) => (
						<Flex
							key={lead._id ?? i}
							px={4}
							py={2}
							gap={3}
							align='center'
							borderTopWidth={i ? '1px' : 0}
							borderColor={ui.borderSubtle}
							bg={ui.surface}
						>
							<Flex
								align='center'
								justify='center'
								boxSize='28px'
								borderRadius='full'
								bg={ui.accentSubtle}
								color={ui.accentInk}
								fontSize='xs'
								fontWeight='bold'
								flexShrink={0}
							>
								{String(lead.leadName ?? '?').trim().charAt(0).toUpperCase() || '?'}
							</Flex>
							<Box minW={0} flex='1'>
								<Text fontSize='sm' color={ui.textPrimary} noOfLines={1}>
									{lead.leadName || 'Unnamed lead'}
								</Text>
								<Text fontSize='xs' color={ui.textMuted} noOfLines={1}>
									{lead.leadWhatsappNumber || lead.leadPhoneNumber || 'No number'}
									{lead.eLeadStatus ? ` · ${lead.eLeadStatus}` : ''}
									{lead.leadStatus ? ` / ${lead.leadStatus}` : ''}
								</Text>
							</Box>
						</Flex>
					))}
					{total > leads.length && (
						<Text px={4} py={2} fontSize='xs' color={ui.textMuted} bg={ui.surface} borderTopWidth='1px' borderColor={ui.borderSubtle}>
							…and {Number(total - leads.length).toLocaleString()} more.
						</Text>
					)}
				</Box>
			)}
		</Box>
	);
}

export default function StepAudience({ campaign, draft, patch, flush, reloadCampaign, setStepBusy }) {
	const ui = useCampaignUI();
	const notify = useAppToast();
	const fileRef = useRef(null);

	const audience = draft.audience ?? {};
	const type = audience.type ?? 'crm';
	const filter = audience.leadFilter ?? {};

	const [uploading, setUploading] = useState(false);
	const [uploadNeedsReload, setUploadNeedsReload] = useState(false);
	const uploadLock = useRef(false);
	const [report, setReport] = useState(null);
	const [attestation, setAttestation] = useState(audience.consentAttestation?.text ?? '');
	const [mapping, setMapping] = useState({ phone: 'Phone', name: 'Name', country: '' });

	const user = useMemo(readUser, []);
	const tree = useSelector((state) => state.user?.tree) ?? { managers: [], agents: {} };

	const setType = (next) => patch({ audience: { ...audience, type: next } });

	// The advanced search form is Formik-shaped; the draft holds only what was
	// actually chosen. The two are kept in step below.
	const formik = useFormik({
		initialValues: { ...EMPTY_FILTER, ...filter },
		validationSchema: validationLeadSearchSchema,
		onSubmit: () => {},
	});
	const { values, errors, touched, handleChange, handleBlur, setFieldValue, setValues } = formik;

	const current = useMemo(() => cleaned(values), [values]);
	const settled = useDebounced(current, SETTLE_MS);

	// Form to draft immediately; only the expensive preview is debounced. Never the other way round on every
	// render: a segment load writes the form explicitly (below), and everything
	// else the draft learns about the filter, it learns from here.
	useEffect(() => {
		if (!same(current, filter)) patch({ audience: { ...audience, leadFilter: current } });
		// `audience`/`filter` are read for comparison only; reacting to them
		// would echo the write we just made.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current]);

	const loadSegment = (next) => {
		setValues({ ...EMPTY_FILTER, ...(next ?? {}) });
		patch({ audience: { ...audience, leadFilter: cleaned(next ?? {}) } });
	};

	const filterJson = useMemo(() => (Object.keys(settled).length ? JSON.stringify(settled) : ''), [settled]);

	const onUpload = async (file) => {
		if (!file || uploadLock.current || uploadNeedsReload) return;
		if (!attestation.trim()) {
			notify.warning('Confirm consent first', 'We need a record that these people agreed to be messaged.');
			return;
		}
		uploadLock.current = true;
		let acceptedButUnrefreshed = false;
		setUploading(true); setStepBusy?.(true);
		try {
			await flush();
			const result = await uploadAudience({ campaignId: campaign._id, file, mapping, attestation });
			if (!result.ok) {
				notify.error('Upload failed', campaignError(result));
				return;
			}
			setReport(result.body.report);
			acceptedButUnrefreshed = true;
			const refreshed = await reloadCampaign();
			if (!refreshed?.audience) throw new Error('Upload was accepted but the audience could not be refreshed. Reopen the draft before continuing.');
			patch({ audience: refreshed.audience });
			acceptedButUnrefreshed = false;
			notify.success(`${result.body.report.valid} contacts read`);
		} catch (err) {
			notify.error('Upload failed', campaignError(err));
		} finally {
			uploadLock.current = false;
			setUploading(false); setUploadNeedsReload(acceptedButUnrefreshed);
			setStepBusy?.(acceptedButUnrefreshed);
			if (fileRef.current) fileRef.current.value = '';
		}
	};

	if (uploadNeedsReload) return <Banner status='warning' title='The upload was accepted. Reopen this draft before continuing.' description='The updated audience could not be read back. Editing is blocked so the saved upload cannot be overwritten by older data.'><Button mt={3} onClick={() => window.location.reload()}>Reload saved draft</Button></Banner>;

	return (
		<Box>
			<SectionCard title='Where do these people come from?' mb={5}>
				<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} role='radiogroup' aria-label='Audience source'>
					<ChoiceCard
						selected={type === 'crm'}
						onSelect={() => setType('crm')}
						icon={RiTeamLine}
						title='From the CRM'
						description='Search your leads the way the lead list does. Resolved with your own visibility — you can never reach a lead you could not already see.'
					/>
					{/* <ChoiceCard
						selected={type === 'upload'}
						onSelect={() => setType('upload')}
						icon={RiFileExcel2Line}
						title='From a file'
						description='A CSV or Excel list. Numbers that match a lead are linked to it; the rest can become new leads.'
					/> */}
				</SimpleGrid>
			</SectionCard>

			{type === 'upload' ? (
				<SectionCard title='Upload your list' subtitle='The file is read and discarded — we never store it.'>
					<Box mb={4}>
						<Banner
							status='warning'
							title='You are confirming these people agreed to be messaged'
							description='WhatsApp requires opt-in for every business-initiated message. Your confirmation is recorded against this campaign with your name and the file’s fingerprint.'
						/>
					</Box>

					<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium' mb={1}>
						Your confirmation
					</Text>
					<Textarea
						size='sm'
						rows={2}
						value={attestation}
						onChange={(e) => setAttestation(e.target.value)}
						placeholder='e.g. These contacts opted in at our September property exhibition.'
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
						mb={4}
					/>

					<SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={2}>
						{[
							{ key: 'phone', label: 'Phone column', required: true },
							{ key: 'name', label: 'Name column' },
							{ key: 'country', label: 'Country column' },
						].map((field) => (
							<Box key={field.key}>
								<Text fontSize='xs' color={ui.textMuted} mb={1}>
									{field.label}
									{field.required ? ' (required)' : ''}
								</Text>
								<Input
									size='sm'
									value={mapping[field.key]}
									onChange={(e) => setMapping((m) => ({ ...m, [field.key]: e.target.value }))}
									bg={ui.inputBg}
									borderColor={ui.border}
									color={ui.textPrimary}
								/>
							</Box>
						))}
					</SimpleGrid>
					<Text fontSize='xs' color={ui.textMuted} mb={4}>
						The column headings exactly as they appear in the first row of your file.
					</Text>

					{/* Belongs with the upload, because it is about uploaded numbers. */}
					<Checkbox
						size='sm'
						
						mb={4}
						isChecked={(audience.unmatchedPolicy ?? 'create_lead') === 'create_lead'}
						onChange={(e) =>
							patch({
								audience: { ...audience, unmatchedPolicy: e.target.checked ? 'create_lead' : 'campaign_only' },
							})
						}
					>
						<Text fontSize='sm' color={ui.textSecondary}>
							Create a lead for each number that matches no lead
						</Text>
					</Checkbox>

					<input
						ref={fileRef}
						type='file'
						accept='.csv,.xlsx'
						style={{ display: 'none' }}
						onChange={(e) => onUpload(e.target.files?.[0])}
					/>
					<Button
						size='sm'
						leftIcon={<RiUploadLine />}
						
						
						isLoading={uploading}
						onClick={() => fileRef.current?.click()} variant='brand'
					>
						Choose a file
					</Button>

					{report && (
						<Box mt={5}>
							<SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
								{[
									['Rows read', report.total],
									['Usable numbers', report.valid],
									['Matched a lead', report.matched],
									['New leads created', report.created],
								].map(([label, value]) => (
									<Box key={label}>
										<Text fontSize='xs' color={ui.textMuted}>
											{label}
										</Text>
										<Text fontSize='lg' color={ui.textPrimary} fontWeight='semibold'>
											{Number(value ?? 0).toLocaleString()}
										</Text>
									</Box>
								))}
							</SimpleGrid>
							{(report.invalid > 0 || report.duplicates > 0) && (
								<Banner
									status='warning'
									title={`${report.invalid} row(s) could not be read, ${report.duplicates} duplicate(s) skipped`}
									description={
										report.rowErrors?.length
											? `First problem: row ${report.rowErrors[0].row} — ${report.rowErrors[0].reason}`
											: undefined
									}
								/>
							)}
						</Box>
					)}
				</SectionCard>
			) : (
				<SectionCard
					title='Find your leads'
					subtitle='Leave everything blank to reach every lead you can see. Every field matches exactly the way the lead list does.'
				>
					{/* ★ REPLACES THE FILTER RATHER THAN MERGING INTO IT — see SegmentBar. */}
					<SegmentBar filter={current} onLoad={loadSegment} />

					<Box
						mx={{ base: -1, md: 0 }}
						sx={{
							// The lead list's form labels use its own token; inside this
							// shell they should read as this screen's secondary text.
							'label, .chakra-form__label': { color: ui.textSecondary },
						}}
					>
						<React.Suspense
							fallback={
								<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
									{Array.from({ length: 6 }).map((_, i) => (
										<Skeleton key={i} h='58px' borderRadius='md' />
									))}
								</SimpleGrid>
							}
						>
							<LazyAdvancedSearchForm
								values={values}
								errors={errors}
								touched={touched}
								handleChange={handleChange}
								handleBlur={handleBlur}
								setFieldValue={setFieldValue}
								user={user}
								tree={tree}
							/>
						</React.Suspense>
					</Box>

					<MatchPreview filterJson={filterJson} enabled={type === 'crm'} />

					<Text mt={4} fontSize='xs' color={ui.textMuted}>
						A contact whose WhatsApp status is “not yet known” is still worth messaging — a failed send costs
						nothing, and the attempt is how the status becomes known.
					</Text>
				</SectionCard>
			)}
		</Box>
	);
}
